import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Header from "@/components/layout/Header";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { ShieldCheck, Building2, Shield, LogIn } from "lucide-react";

export default function ClaimInviteAccept() {
  const { token } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/live/claim-invites/${token}`).then(({ data }) => setInvite(data))
      .catch((e) => setError(e?.response?.data?.detail || "Invalid claim link"));
  }, [token]);

  const accept = () => {
    setBusy(true);
    api.post(`/live/claim-invites/${token}/accept`, { account_created: false })
      .then(({ data }) => {
        toast.success(`You now own ${data.target_name}`);
        navigate(data.target_type === "team" && data.slug ? `/live/teams/${data.slug}` : "/live");
      })
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to accept")).finally(() => setBusy(false));
  };

  return (
    <div className="min-h-screen bg-[#F1F3F6]"><Header />
      <div className="max-w-lg mx-auto px-6 py-16" data-testid="claim-invite-page">
        <div className="bg-white border border-slate-200 rounded-sm p-8 text-center">
          {error ? <div data-testid="claim-invite-error" className="text-sm text-[#E63946]">{error}</div>
            : !invite ? <div className="text-sm text-slate-400">Loading invite…</div>
            : (
              <>
                <div className="w-14 h-14 rounded-full bg-[#E63946]/10 grid place-items-center mx-auto">
                  {invite.target_type === "organization" ? <Building2 className="w-6 h-6 text-[#E63946]" /> : <Shield className="w-6 h-6 text-[#E63946]" />}
                </div>
                <div className="label-overline text-[#E63946] mt-4">CBR Ownership Invite</div>
                <h1 className="font-display text-2xl uppercase tracking-tight mt-1" data-testid="claim-invite-target">{invite.target_name}</h1>
                <p className="text-sm text-slate-500 mt-2">CBR has invited you to take ownership of this {invite.target_type}. Accepting gives you full management access — all existing history, rosters, games and rankings are preserved.</p>
                {user ? (
                  <button data-testid="accept-claim-btn" disabled={busy} onClick={accept}
                    className="mt-6 w-full flex items-center justify-center gap-2 bg-[#E63946] text-white text-sm font-semibold uppercase tracking-wider py-3 rounded-sm hover:bg-[#c62f3b] disabled:opacity-50">
                    <ShieldCheck className="w-4 h-4" /> Accept & Become Owner
                  </button>
                ) : (
                  <div className="mt-6">
                    <p className="text-xs text-slate-400 mb-2">Sign in or create an account to accept this invite. You'll return here to confirm.</p>
                    <div className="flex gap-2">
                      <Link to="/login" data-testid="claim-invite-login" className="flex-1 flex items-center justify-center gap-1.5 bg-[#0A192F] text-white text-sm font-semibold uppercase tracking-wider py-3 rounded-sm"><LogIn className="w-4 h-4" /> Sign In</Link>
                      <Link to="/signup" data-testid="claim-invite-signup" className="flex-1 border border-slate-200 text-[#0A192F] text-sm font-semibold uppercase tracking-wider py-3 rounded-sm grid place-items-center">Create Account</Link>
                    </div>
                  </div>
                )}
              </>
            )}
        </div>
      </div>
    </div>
  );
}
